import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

const IOS_DISMISS_KEY = 'ftb_ios_install_dismissed_at';
const IOS_DISMISS_DAYS = 14;

function isIos() {
    const ua = window.navigator.userAgent;
    // iPadOS 13+ meldt zich als "Macintosh" maar heeft wél touch-support.
    return /iphone|ipad|ipod/i.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

function isStandalone() {
    return window.navigator.standalone === true
        || window.matchMedia('(display-mode: standalone)').matches;
}

function InstallPrompt() {
    const intl = useIntl();
    const location = useLocation();
    // Klanten die hier enkel komen boeken/een boeking beheren/feedback geven
    // hebben niets aan "app installeren" (dat is voor de aanbieder zelf, op
    // diens eigen dashboard/instellingen) — geen zichtbare knop/banner tonen
    // op deze publieke, klant-gerichte pagina's. De 'beforeinstallprompt'-
    // listener hieronder blijft wel overal actief (ook hier): die roept
    // preventDefault() aan, wat de EIGEN automatische installatiebalk van de
    // browser onderdrukt — zonder die listener zou de browser op deze pagina
    // alsnog zijn eigen, niet te onderdrukken install-UI kunnen tonen.
    const isPublicCustomerRoute = /^\/(book|manage-booking)\//.test(location.pathname);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showIosBanner, setShowIosBanner] = useState(false);

    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        const handleAppInstalled = () => {
            setDeferredPrompt(null);
            setShowIosBanner(false);
        };
        window.addEventListener('appinstalled', handleAppInstalled);

        if (isIos() && !isStandalone()) {
            let dismissedAt = null;
            try {
                dismissedAt = window.localStorage.getItem(IOS_DISMISS_KEY);
            } catch (e) {
                // Privénavigatie/geblokkeerde storage: banner gewoon tonen.
            }
            const daysSinceDismiss = dismissedAt
                ? (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24)
                : Infinity;
            if (daysSinceDismiss > IOS_DISMISS_DAYS) {
                setShowIosBanner(true);
            }
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallClick = () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            deferredPrompt.userChoice.then((choiceResult) => {
                if (choiceResult.outcome === 'accepted') {
                    console.log('Gebruiker accepteerde de installatie');
                }
                setDeferredPrompt(null);
            });
        }
    };

    const handleDismissIos = () => {
        setShowIosBanner(false);
        try {
            window.localStorage.setItem(IOS_DISMISS_KEY, String(Date.now()));
        } catch (e) {
            // Kan niet onthouden worden zonder storage — niet blokkerend.
        }
    };

    if (deferredPrompt && !isPublicCustomerRoute) {
        return (
            <div style={{
                position: 'fixed',
                bottom: '20px',
                left: '20px',
                backgroundColor: '#007bff',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '18px',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.2)',
                fontFamily: "'Poppins', system-ui, -apple-system, sans-serif",
                fontWeight: 600,
                cursor: 'pointer',
                zIndex: 1000
            }} onClick={handleInstallClick}>
                {intl.formatMessage({ id: 'install.androidButton' })}
            </div>
        );
    }

    if (showIosBanner && !isPublicCustomerRoute) {
        return (
            <div style={{
                position: 'fixed',
                left: '12px',
                right: '12px',
                bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
                backgroundColor: '#ffffff',
                color: '#1e293b',
                borderRadius: '18px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.18)',
                padding: '16px 44px 16px 16px',
                fontFamily: "'Open Sans', system-ui, -apple-system, sans-serif",
                zIndex: 1000
            }}>
                <button
                    onClick={handleDismissIos}
                    aria-label={intl.formatMessage({ id: 'install.dismiss' })}
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        border: 'none',
                        background: 'transparent',
                        fontSize: '18px',
                        lineHeight: 1,
                        color: '#94a3b8',
                        cursor: 'pointer',
                        padding: '4px'
                    }}
                >
                    ×
                </button>
                <div style={{
                    fontFamily: "'Poppins', system-ui, -apple-system, sans-serif",
                    fontWeight: 600,
                    marginBottom: '4px'
                }}>
                    {intl.formatMessage({ id: 'install.iosTitle' })}
                </div>
                <div style={{ fontSize: '14px', color: '#475569' }}>
                    {intl.formatMessage({ id: 'install.iosInstruction' })}
                </div>
            </div>
        );
    }

    return null;
}

export default InstallPrompt;
